import React, { useState } from "react";
import { connect } from "react-redux";

import { bindActionCreators, Dispatch } from "redux";

import { FilterSection, FilterItem, FilterItemField, PickerInput, DataColumnDef } from "@plex/react-components";

import { arrayHelpers } from "infrastructure/helpers";
import { commonMessages, ILocale } from "infrastructure/localization";
import { useLocalizedValues } from "ui/hooks";
import { glossaryMessages } from "ui/screens/plex-dictionary/plex-dictionary.localization";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";
import { IIndustry } from "ui/shared/models/common";

import { translationHistoryActions } from "../TranslationHistory.actions";
import { ITranslationHistoryState, ITranslationHistoryFilterData } from "../TranslationHistory.state";

interface ITranslationHistoryFilterProps {
  applyTranslationHistoryFilter: (filter: ITranslationHistoryFilterData) => void;
  loadIndustries: (criterion: IPickerSearchCriterion<IIndustry>) => void;
  loadLocales: (criterion: IPickerSearchCriterion<ILocale>) => void;
  locales?: ILocale[];
  industries?: IIndustry[];
}

const TranslationsHistoryFilter: React.FC<ITranslationHistoryFilterProps> = ({ applyTranslationHistoryFilter, locales, loadLocales, industries, loadIndustries }) => {
  const [filter, setValues] = useState<ITranslationHistoryFilterData>({ pinned: false });

  const localizedValues = useLocalizedValues(() => ({
    locale: glossaryMessages.filter("locale"),
    industry: glossaryMessages.filter("industry"),
    localeIdentifier: commonMessages.property("localeIdentifier"),
    name: commonMessages.property("localeName"),
    code: glossaryMessages.property("code"),
    description: glossaryMessages.property("description")
  }));

  const onFilterSelect = <T, P extends keyof ITranslationHistoryFilterData>(items: T[], prop: P) => {
    setValues({ ...filter, [prop]: items && items.length ? items[0] : undefined });
  };

  return (
    <FilterSection pinned={filter.pinned} onSearch={() => applyTranslationHistoryFilter(filter)} onPin={() => setValues({ ...filter, pinned: !filter.pinned })}>
      <FilterItem labelText={localizedValues.locale}>
        <FilterItemField shouldCollapse={() => !filter.currentLocale}>
          <PickerInput
            autoPick
            dialogTitle={localizedValues.locale}
            data={locales}
            keySelector={row => row.localeIdentifier}
            displaySelector={row => (row ? row.name : "")}
            selected={arrayHelpers.createFrom(filter.currentLocale)}
            onSelectionChanged={rows => onFilterSelect(rows, "currentLocale")}
            onSearch={(q, cols) => loadLocales({ query: q, searchedColumns: cols })}
          >
            <DataColumnDef<ILocale> id="localeIdentifier" title={localizedValues.localeIdentifier} valueSelector={row => row.localeIdentifier} searchable sortable>
              {row => row.localeIdentifier}
            </DataColumnDef>
            <DataColumnDef<ILocale> id="name" title={localizedValues.name} valueSelector={row => row.name} searchable sortable>
              {row => row.name}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.industry}>
        <FilterItemField shouldCollapse={() => !filter.currentIndustry}>
          <PickerInput
            autoPick
            dialogTitle={localizedValues.industry}
            data={industries}
            keySelector={row => row.isiCv4Code}
            displaySelector={row => row.description}
            selected={arrayHelpers.createFrom(filter.currentIndustry)}
            onSelectionChanged={rows => onFilterSelect(rows, "currentIndustry")}
            onSearch={(q, cols) => loadIndustries({ query: q, searchedColumns: cols })}
          >
            <DataColumnDef<IIndustry> id="isiCv4Code" title={localizedValues.code} valueSelector={row => row.isiCv4Code} searchable>
              {row => row.isiCv4Code}
            </DataColumnDef>
            <DataColumnDef<IIndustry> id="description" title={localizedValues.description} valueSelector={row => row.description} searchable>
              {row => row.description}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
    </FilterSection>
  );
};

export default connect(
  (state: { translationHistory: ITranslationHistoryState }) => {
    const filter = state.translationHistory.filter;
    return {
      locales: filter.locales,
      industries: filter.industries
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        applyTranslationHistoryFilter: translationHistoryActions.applyTranslationHistoryFilter,
        loadLocales: translationHistoryActions.loadLocales,
        loadIndustries: translationHistoryActions.loadIndustries
      },
      dispatch
    );
  }
)(TranslationsHistoryFilter);
